import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { Alert, Col, FormGroup, Label, Spinner } from 'reactstrap';
import { t } from 'autobiz-translate';
import { AutobizButton } from 'autobiz-strap';
import { Field, Form } from 'react-final-form';
import { updateUserAlcopaUseCase } from '../../../../hexagon/usecases/updateUserAlcopaAccess/updateUserAlcopaUseCase';
import { userAlcopaCredentialSelector } from '../../view-models-generators/userAlcopaCredentialSelector';
import { getUserSelector } from '../../view-models-generators/userSelector';

const UserAlcopa: React.FC = () => {
    const dispatch = useDispatch();
    const [alertOpen, setAlertOpen] = useState<boolean>(false);
    const [canSubmit, setCanSubmit] = useState<boolean>(false);
    const { user } = useSelector(getUserSelector);
    const { updateUserAlcopaCredentialStatus: alcopaStatus } = useSelector(
        userAlcopaCredentialSelector,
    );

    useEffect(() => {
        if (['succeeded', 'failed'].includes(alcopaStatus)) setAlertOpen(true);
    }, [alcopaStatus]);

    const validationSchema: Yup.SchemaOf<any> = Yup.object().shape({
        alcopaUsername: Yup.string().email().required(),
        alcopaPassword: Yup.string().min(6).required(),
    });

    const validate = (values: any): Promise<any> =>
        validationSchema
            .validate(values, { abortEarly: false })
            .then(() => setCanSubmit(true))
            .catch((error: Yup.ValidationError) => {
                const errors: { [value: string]: string } = {};
                error.inner.forEach((e) => {
                    if (e.path) {
                        errors[e.path] = e.message;
                    }
                });
                setCanSubmit(false);

                return errors;
            });

    const onSubmit = async (props: any) => {
        if (!props.alcopaUsername && !props.alcopaPassword) return;

        dispatch(
            updateUserAlcopaUseCase({
                username: props.alcopaUsername,
                password: props.alcopaPassword,
                autobizUserId: user.autobizUserId as string,
            }),
        );
        setAlertOpen(false);
    };

    return (
        <>
            <span
                dangerouslySetInnerHTML={{
                    __html: t('explanationAlcopaProfilePage'),
                }}
            />
            {alcopaStatus === 'succeeded' && (
                <Alert
                    color="success"
                    className="mt-4"
                    fade
                    isOpen={alertOpen}
                    toggle={() => setAlertOpen(false)}
                >
                    {t('updateUserProfileSucceed')}
                </Alert>
            )}

            {alcopaStatus === 'failed' && (
                <Alert
                    color="danger"
                    className="mt-4"
                    fade
                    isOpen={alertOpen}
                    toggle={() => setAlertOpen(false)}
                >
                    {t('updateUserProfileFailed')}
                </Alert>
            )}
            {alcopaStatus === 'loading' ? (
                <div className="m-3 text-center">
                    <Spinner />
                </div>
            ) : (
                <Form
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={{ alcopaPassword: '', alcopaUsername: '' }}
                    render={({ handleSubmit, pristine }) => (
                        <form onSubmit={handleSubmit} className="mt-5">
                            <div>
                                <Field
                                    name="alcopaUsername"
                                    render={({ input, meta }) => (
                                        <FormGroup
                                            className={
                                                meta.error && meta.touched
                                                    ? 'row form-group-error'
                                                    : 'row'
                                            }
                                        >
                                            <Col sm={6} lg={8}>
                                                <Label>{t('email')}</Label>
                                            </Col>

                                            <Col sm="6" lg={4} className="userInputs">
                                                <input
                                                    type="text"
                                                    {...input}
                                                    value={input.value}
                                                    autoComplete="off"
                                                    className={`form-control text-right ${
                                                        meta.error &&
                                                        meta.touched &&
                                                        'error-control'
                                                    }`}
                                                />
                                            </Col>
                                        </FormGroup>
                                    )}
                                />
                            </div>

                            <div>
                                <Field
                                    name="alcopaPassword"
                                    render={({ input, meta }) => (
                                        <FormGroup
                                            className={
                                                meta.error && meta.touched
                                                    ? 'row form-group-error'
                                                    : 'row'
                                            }
                                        >
                                            <Col sm={6} lg={8}>
                                                <Label>{t('password')}</Label>
                                            </Col>

                                            <Col sm="6" lg={4} className="userInputs">
                                                <input
                                                    type="password"
                                                    {...input}
                                                    value={input.value}
                                                    autoComplete="new-password"
                                                    className={`form-control text-right ${
                                                        meta.error &&
                                                        meta.touched &&
                                                        'error-control'
                                                    }`}
                                                />
                                            </Col>
                                        </FormGroup>
                                    )}
                                />
                            </div>

                            <AutobizButton
                                type="submit"
                                color="primary"
                                disabled={!canSubmit || pristine}
                                className={`mx-auto mt-5 ${!canSubmit}`}
                            >
                                {t('valider')}
                            </AutobizButton>
                        </form>
                    )}
                />
            )}
        </>
    );
};
export default UserAlcopa;
